import * as Yup from 'yup';

// Matches the backend's AgeEligibilityService: India-only age gate, three tiers.
const calculateAge = (dob: string): number => {
  const birthDate = new Date(dob);
  const today = new Date();
  return (
    today.getFullYear() -
    birthDate.getFullYear() -
    (today < new Date(today.getFullYear(), birthDate.getMonth(), birthDate.getDate()) ? 1 : 0)
  );
};

// validation schema
export const signupSchema = Yup.object().shape({
  firstName: Yup.string()
    .required('Your first name is required.')
    .min(1, 'Your first name is required to be at least 1 character.')
    .max(50, 'Your first name cannot be longer than 50 characters.')
    .matches(/^[a-zA-Z]+$/, 'First name cannot contain numbers or special characters'),
  lastName: Yup.string()
    .required('Your last name is required.')
    .min(1, 'Your last name is required to be at least 1 character.')
    .max(50, 'Your last name cannot be longer than 50 characters.')
    .matches(/^[a-zA-Z]+$/, 'Last name cannot contain numbers or special characters'),
  email: Yup.string()
    .required('Your email is required.')
    .min(5, 'Your email is required to be at least 5 characters.')
    .max(254, 'Your email cannot be longer than 254 characters.')
    .matches(/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/, 'Your email is invalid.'),
  password: Yup.string()
    .required('Your password is required.')
    .min(8, 'Your password is required to be at least 8 characters.')
    .max(50, 'Your password cannot be longer than 50 characters.')
    .matches(
      /^(?!\s)(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d])[A-Za-z\d\S]{8,}$/,
      'Password must contain at least one lowercase letter, one uppercase letter, one digit, one special character, and no white spaces.'
    ),
  confirmPassword: Yup.string()
    .required('Your confirmation password is required.')
    .min(4, 'Your confirmation password is required to be at least 4 characters.')
    .max(50, 'Your confirmation password cannot be longer than 50 characters.')
    .oneOf([Yup.ref('password')], 'The password and its confirmation do not match!'),
  dateOfBirth: Yup.string()
    .required('Your date of birth is required.')
    .test('not-underage', 'You must be at least 16 years old to create an account.', function (dob: string) {
      const countryCode = this.parent?.countryCode;
      if (!dob || countryCode !== '+91') return true;
      return calculateAge(dob) >= 16;
    }),
  countryCode: Yup.string(),
  phoneNumber: Yup.string(),
  isoCode: Yup.string(),
  parentEmail: Yup.string()
    .matches(/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/, {
      message: 'Please enter a valid parent email address.',
      excludeEmptyString: true,
    })
    .notOneOf([Yup.ref('email')], 'Parent email cannot be the same as your own email.')
    .when(['dateOfBirth', 'countryCode'], {
      is: (dob: string, countryCode: string) => {
        if (!dob || countryCode !== '+91') return false;
        const age = calculateAge(dob);
        return age >= 16 && age < 18;
      },
      then: schema => schema.required('Parent or guardian email is required for Indian users aged 16 and 17 (DPDP Act).'),
      otherwise: schema => schema.notRequired(),
    }),
});
